import { z } from "zod";

import { EMPTY_FILTERS as NOTICE_EMPTY_FILTERS, NoticeFilters } from "components/notices/utils";
import { NoticeTopicSearchInput } from "lib/generated/app-service-gql/graphql";
import { NoticesStagesv2, NoticesTopicSearchInputStagesFilter } from "lib/types/graphQLEnums";
import { DateFilterSchema, savedNoticeSemanticViewFilters } from "lib/types/savedViews";
import {
  type NoticeSemanticSort,
  noticesSortOrderToGraphQLEnum,
  semanticFieldToNoticesSort,
} from "./noticeSemanticSort";

export const NoticeV2FiltersSchema = savedNoticeSemanticViewFilters.shape.filterSettings;

export type NoticeV2Filters = z.infer<typeof NoticeV2FiltersSchema>;

/** Default filter values. Use NoticeV2FiltersSchema.parse(partial) to fill in defaults for partial input. */
export const DEFAULT_FILTERS: NoticeV2Filters = NoticeV2FiltersSchema.parse({});

/**
 * Converts NoticeV2Filters to NoticeFilters for use with shared column renderers
 * (NewTitleCell, FrameworkCell, etc.) that expect the legacy filter shape.
 */
export function noticeV2FiltersToNoticeFilters(filters: NoticeV2Filters): NoticeFilters {
  return {
    ...NOTICE_EMPTY_FILTERS,
    sort: { field: "relevance_score", order: "DESC" },
    text: filters.topicSearch,
    stage: filters.stage as NoticesStagesv2[],
    buyers: filters.buyerIds ?? [],
    buyerListIds: filters.buyerListIds ?? [],
    buyerCategories: filters.buyerCategories ?? [],
    supplierGuids: filters.supplierIds ?? [],
    frameworkIds: filters.frameworkIds ?? [],
    publishDate: filters.publishDate ?? {},
    value: filters.value,
    expiryDate: {
      filter: {
        from: filters.expiryDate?.from,
        to: filters.expiryDate?.to,
        relativeFrom: filters.expiryDate?.relativeFrom,
        relativeTo: filters.expiryDate?.relativeTo,
      },
      hideNulls: filters.expiryDate?.hideNulls,
    },
  };
}

function hasDateRange(dateFilter: z.infer<typeof DateFilterSchema> | undefined): boolean {
  if (!dateFilter || typeof dateFilter !== "object") return false;
  const { from, to, relativeFrom, relativeTo } = dateFilter;
  return [from, to, relativeFrom, relativeTo].some(
    (v) => v !== undefined && v !== null && v !== "",
  );
}

export function getAppliedFilters(filters: NoticeV2Filters): (keyof NoticeV2Filters)[] {
  const applied: (keyof NoticeV2Filters)[] = [];

  if (filters.stage.length > 0) {
    applied.push("stage");
  }
  if (filters.buyerIds.length > 0) {
    applied.push("buyerIds");
  }
  if (filters.buyerListIds.length > 0) {
    applied.push("buyerListIds");
  }
  if (filters.buyerCategories.length > 0) {
    applied.push("buyerCategories");
  }
  if (filters.supplierIds.length > 0) {
    applied.push("supplierIds");
  }
  if (filters.frameworkIds.length > 0) {
    applied.push("frameworkIds");
  }
  if (hasDateRange(filters.publishDate)) {
    applied.push("publishDate");
  }
  if (hasDateRange(filters.expiryDate) || filters.expiryDate?.hideNulls) {
    applied.push("expiryDate");
  }
  if (
    filters.value?.from !== undefined ||
    filters.value?.to !== undefined ||
    filters.value?.hideNulls === true
  ) {
    applied.push("value");
  }

  return applied;
}

export function convertNoticeV2FiltersToSearchRequest(
  filters: NoticeV2Filters,
  pagination: { current: number; pageSize: number },
  sort: NoticeSemanticSort,
): NoticeTopicSearchInput {
  const hasStage = (filters.stage?.length ?? 0) > 0;
  const hasBuyerIds = (filters.buyerIds?.length ?? 0) > 0;
  const hasBuyerLists = (filters.buyerListIds?.length ?? 0) > 0;
  const hasBuyerCategories = (filters.buyerCategories?.length ?? 0) > 0;
  const hasBuyers = hasBuyerIds || hasBuyerLists || hasBuyerCategories;
  const hasSuppliers = (filters.supplierIds?.length ?? 0) > 0;
  const hasFrameworks = (filters.frameworkIds?.length ?? 0) > 0;
  const hasPublishDate = hasDateRange(filters.publishDate);
  const hasExpiryDate = hasDateRange(filters.expiryDate) || filters.expiryDate?.hideNulls;
  const hasContractValue =
    filters.value?.from !== undefined ||
    filters.value?.to !== undefined ||
    filters.value?.hideNulls === true;
  const filtersPayload = {
    ...(hasStage && {
      stages: filters.stage as NoticesTopicSearchInputStagesFilter[],
    }),
    ...(hasBuyers && {
      buyerFilters: {
        ...(hasBuyerIds && { buyers: filters.buyerIds }),
        ...(hasBuyerLists && { buyerLists: filters.buyerListIds }),
        ...(hasBuyerCategories && { buyerCategories: filters.buyerCategories }),
      },
    }),
    ...(hasContractValue && {
      value: {
        ...(filters.value?.from !== undefined && { from: filters.value.from }),
        ...(filters.value?.to !== undefined && { to: filters.value.to }),
        ...(filters.value?.hideNulls === true && { hideNulls: true }),
      },
    }),
    ...(hasSuppliers && { suppliers: filters.supplierIds }),
    ...(hasFrameworks && { frameworks: filters.frameworkIds }),
    ...(hasPublishDate && {
      publishDate: {
        filter: {
          from: filters.publishDate?.from,
          to: filters.publishDate?.to,
          relativeFrom: filters.publishDate?.relativeFrom,
          relativeTo: filters.publishDate?.relativeTo,
        },
      },
    }),
    ...(hasExpiryDate && {
      expiryDate: {
        filter: {
          from: filters.expiryDate?.from,
          to: filters.expiryDate?.to,
          relativeFrom: filters.expiryDate?.relativeFrom,
          relativeTo: filters.expiryDate?.relativeTo,
        },
        hideNulls: filters.expiryDate?.hideNulls,
      },
    }),
    sort: semanticFieldToNoticesSort(sort.field),
    sortOrder: noticesSortOrderToGraphQLEnum(sort.order),
  };

  return {
    topicSearch: filters.topicSearch,
    page: pagination.current,
    limit: pagination.pageSize,
    filters: filtersPayload,
  };
}

export const stageUnionToEnum = (stages: `${NoticesStagesv2}`[]): NoticesStagesv2[] => {
  return stages as NoticesStagesv2[];
};
